import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { CodeBranchIcon, CodeCommitIcon, TagOffIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { GitReference } from "@octopusdeploy/octopus-server-client";
import { toGitCommitShort, toGitRefShort, isGitCommit, isGitTag } from "@octopusdeploy/octopus-server-client";
import React from "react";
interface GitRefLinkProps {
    gitRef: GitReference;
    commitUrl: string | null;
}
export const GitRefLink = ({ gitRef, commitUrl }: GitRefLinkProps) => {
    if (!gitRef.GitRef) {
        return null;
    }
    const iconSize = 16;
    const tagOffIcon = <TagOffIcon size={iconSize} ariaLabel="Git tag icon"/>;
    const codeBranchIcon = <CodeBranchIcon size={iconSize} ariaLabel="Git branch icon"/>;
    const gitRefIcon = isGitTag(gitRef.GitRef) ? tagOffIcon : codeBranchIcon;
    const refDetails = !isGitCommit(gitRef.GitRef) && (<Tooltip key="ref" content={gitRef.GitRef}>
            <div className={styles.container}>
                {gitRefIcon}
                <span className={styles.truncateLabel}>{toGitRefShort(gitRef.GitRef)}</span>
            </div>
        </Tooltip>);
    const commitDetailsContent = gitRef.GitCommit && (<div className={styles.container}>
            <CodeCommitIcon size={iconSize} ariaLabel="Git commit icon"/>
            {toGitCommitShort(gitRef.GitCommit)}
        </div>);
    const commitDetails = commitDetailsContent && (<Tooltip key="commit" content={gitRef.GitCommit}>
            {commitUrl ? (<a className={styles.link} href={commitUrl} target="_blank" rel="noreferrer noopener">
                    {commitDetailsContent}
                </a>) : (commitDetailsContent)}
        </Tooltip>);
    return (<div className={styles.container}>
            {refDetails}
            {commitDetails}
        </div>);
};
const styles = {
    container: css({
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: space[4],
        font: "inherit",
        verticalAlign: "bottom",
    }),
    truncateLabel: css({
        maxWidth: "15rem",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    }),
    link: css({
        color: themeTokens.color.text.link.default,
        textDecoration: "none",
        "&:hover": {
            color: themeTokens.color.text.link.hover,
        },
    }),
};
